import type { FieldConfig, ColumnConfig } from '@/pages/admin/components/EntityManager';

export const leaderFields: FieldConfig[] = [
  { name: 'name', label: 'Full Name', type: 'text', required: true },
  { name: 'title', label: 'Title / Position', type: 'text' },
  { name: 'bio', label: 'Biography', type: 'textarea' },
  { name: 'image', label: 'Photo', type: 'image', help: 'Upload a photo or paste a direct image link' },
  { name: 'sort_order', label: 'Display Order', type: 'number' },
  { name: 'is_published', label: 'Published', type: 'checkbox' },
];

export const leaderColumns: ColumnConfig[] = [
  { key: 'name', label: 'Name' },
  { key: 'title', label: 'Title' },
  { key: 'image', label: 'Photo', type: 'image' },
  { key: 'sort_order', label: 'Order' },
  { key: 'is_published', label: 'Status', type: 'boolean' },
];

export const sermonFields: FieldConfig[] = [
  { name: 'title', label: 'Sermon Title', type: 'text', required: true },
  { name: 'speaker', label: 'Speaker', type: 'text' },
  { name: 'series', label: 'Series', type: 'text' },
  { name: 'scripture', label: 'Scripture Reference', type: 'text' },
  { name: 'category', label: 'Category', type: 'text' },
  { name: 'description', label: 'Description', type: 'textarea' },
  { name: 'video_url', label: 'Video URL (YouTube/Vimeo)', type: 'url' },
  { name: 'audio_url', label: 'Audio URL', type: 'url' },
  { name: 'image', label: 'Thumbnail', type: 'image' },
  { name: 'preached_on', label: 'Date Preached', type: 'date' },
  { name: 'is_featured', label: 'Featured', type: 'checkbox' },
  { name: 'is_published', label: 'Published', type: 'checkbox' },
];

export const sermonColumns: ColumnConfig[] = [
  { key: 'title', label: 'Title' },
  { key: 'speaker', label: 'Speaker' },
  { key: 'series', label: 'Series' },
  { key: 'preached_on', label: 'Date', type: 'date' },
  { key: 'is_featured', label: 'Featured', type: 'boolean' },
  { key: 'is_published', label: 'Status', type: 'boolean' },
];

export const eventFields: FieldConfig[] = [
  { name: 'title', label: 'Event Title', type: 'text', required: true },
  { name: 'description', label: 'Description', type: 'textarea' },
  { name: 'category', label: 'Category', type: 'select', options: [
    { value: 'event', label: 'Event' },
    { value: 'project', label: 'Project (e.g. building a new church)' },
    { value: 'outreach', label: 'Outreach' },
  ], help: 'Choose "Project" for building/fundraising initiatives so visitors see a "Contribute" button.' },
  { name: 'event_date', label: 'Date', type: 'date' },
  { name: 'start_time', label: 'Start Time', type: 'time' },
  { name: 'end_time', label: 'End Time', type: 'time' },
  { name: 'location', label: 'Location', type: 'text' },
  { name: 'organizer', label: 'Organizer', type: 'text' },
  { name: 'registration_url', label: 'Registration URL', type: 'url' },
  { name: 'image', label: 'Main Image', type: 'image', help: 'Used as the cover photo' },
  { name: 'images', label: 'Project Photos', type: 'images', help: 'Upload several photos — visitors can scroll through them on the project card.' },
  { name: 'is_published', label: 'Published', type: 'checkbox' },
];

export const eventColumns: ColumnConfig[] = [
  { key: 'title', label: 'Title' },
  { key: 'category', label: 'Category' },
  { key: 'event_date', label: 'Date', type: 'date' },
  { key: 'location', label: 'Location' },
  { key: 'is_published', label: 'Status', type: 'boolean' },
];

export const newsFields: FieldConfig[] = [
  { name: 'title', label: 'Title', type: 'text', required: true },
  { name: 'slug', label: 'Slug', type: 'text', help: 'URL-friendly identifier (lowercase, hyphens)' },
  { name: 'excerpt', label: 'Excerpt', type: 'textarea' },
  { name: 'content', label: 'Full Content', type: 'textarea' },
  { name: 'category', label: 'Category', type: 'text' },
  { name: 'author', label: 'Author', type: 'text' },
  { name: 'cover_image', label: 'Cover Image', type: 'image' },
  { name: 'read_time', label: 'Read Time', type: 'text' },
  { name: 'published_on', label: 'Publish Date', type: 'date' },
  { name: 'is_featured', label: 'Featured', type: 'checkbox' },
  { name: 'is_published', label: 'Published', type: 'checkbox' },
];

export const newsColumns: ColumnConfig[] = [
  { key: 'cover_image', label: 'Cover', type: 'image' },
  { key: 'title', label: 'Title' },
  { key: 'category', label: 'Category' },
  { key: 'published_on', label: 'Date', type: 'date' },
  { key: 'is_featured', label: 'Featured', type: 'boolean' },
  { key: 'is_published', label: 'Status', type: 'boolean' },
];

export const galleryFields: FieldConfig[] = [
  { name: 'src', label: 'Image', type: 'image', required: true },
  { name: 'title', label: 'Photo Title', type: 'text', placeholder: 'e.g. Sunday Worship Gathering' },
  { name: 'description', label: 'Description', type: 'textarea', placeholder: 'A short caption describing this photo' },
  { name: 'alt', label: 'Alt Text', type: 'text', help: 'Describes the image for accessibility and search engines' },
  { name: 'category', label: 'Category', type: 'category', help: 'Choose an existing category or add a new one' },
  { name: 'sort_order', label: 'Display Order', type: 'number' },
  { name: 'is_published', label: 'Show on Website', type: 'checkbox' },
];

export const galleryColumns: ColumnConfig[] = [
  { key: 'src', label: 'Image', type: 'image' },
  { key: 'title', label: 'Title' },
  { key: 'alt', label: 'Alt Text' },
  { key: 'category', label: 'Category' },
  { key: 'sort_order', label: 'Order' },
  { key: 'is_published', label: 'Visible', type: 'boolean' },
];

export const testimonialFields: FieldConfig[] = [
  { name: 'name', label: 'Name', type: 'text', required: true },
  { name: 'role', label: 'Role', type: 'text' },
  { name: 'rating', label: 'Rating (1-5)', type: 'number' },
  { name: 'text', label: 'Testimonial', type: 'textarea' },
  { name: 'is_published', label: 'Published', type: 'checkbox' },
];

export const testimonialColumns: ColumnConfig[] = [
  { key: 'name', label: 'Name' },
  { key: 'role', label: 'Role' },
  { key: 'rating', label: 'Rating' },
  { key: 'is_published', label: 'Status', type: 'boolean' },
];

export const ministryFields: FieldConfig[] = [
  { name: 'name', label: 'Ministry Name', type: 'text', required: true },
  { name: 'leader', label: 'Ministry Leader', type: 'text' },
  { name: 'meeting_info', label: 'Meeting Information', type: 'text' },
  { name: 'description', label: 'Description', type: 'textarea' },
  { name: 'image', label: 'Image', type: 'image' },
  { name: 'is_published', label: 'Published', type: 'checkbox' },
];

export const ministryColumns: ColumnConfig[] = [
  { key: 'name', label: 'Name' },
  { key: 'leader', label: 'Leader' },
  { key: 'image', label: 'Image', type: 'image' },
  { key: 'is_published', label: 'Status', type: 'boolean' },
];

export const bookFields: FieldConfig[] = [
  { name: 'title', label: 'Book Title', type: 'text', required: true },
  { name: 'subtitle', label: 'Subtitle', type: 'text' },
  { name: 'author', label: 'Author', type: 'text', placeholder: 'Apostle A. C. Oliver' },
  { name: 'description', label: 'Description', type: 'textarea' },
  { name: 'cover_image', label: 'Cover Image', type: 'image', help: 'Upload the book cover, or leave blank to use a designed cover.' },
  { name: 'buy_url', label: 'Buy Link (Amazon, etc.)', type: 'url', help: 'Where visitors go when they click "Buy Now".' },
  { name: 'price', label: 'Price (optional)', type: 'text', placeholder: 'e.g. $14.99' },
  { name: 'sort_order', label: 'Display Order', type: 'number' },
  { name: 'is_published', label: 'Published', type: 'checkbox' },
];

export const bookColumns: ColumnConfig[] = [
  { key: 'cover_image', label: 'Cover', type: 'image' },
  { key: 'title', label: 'Title' },
  { key: 'author', label: 'Author' },
  { key: 'price', label: 'Price' },
  { key: 'sort_order', label: 'Order' },
  { key: 'is_published', label: 'Status', type: 'boolean' },
];